<template>
  <ion-page>
    <ion-content
      :class="['main-background', 'today-content']"
      :fullscreen="true"
      :scroll-y="true"
    >
      <ion-refresher slot="fixed" @ionRefresh="onRefresh">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <header class="ion-text-center today-header">
        <h1 class="header-title white-bold">Сегодня</h1>
        <h3 class="white-bold">{{ now }}</h3>
      </header>

      <!-- Events Section (всегда при авторизации и онлайн) — одна строка, горизонтальный скролл -->
      <div class="events-section" v-if="!globalContext.offlineMode && isAuthenticated">
        <h2 class="section-title">События</h2>
        <div ref="eventsScrollWrap" class="events-scroll-wrap">
          <div class="events-scroll">
            <ion-card
              v-for="ev in upcomingEvents"
              :key="ev.id"
              class="event-card"
              @click="$router.push(`/events/${ev.id}`)"
            >
              <div class="event-image-container">
                <img
                  :src="ev.logoPath ? eventsService.logoUrl(ev.logoPath) : fallbackLogo"
                  :alt="ev.title"
                  class="event-image"
                />
                <div class="event-overlay">
                  <h3 class="event-title">{{ ev.title }}</h3>
                  <p class="event-meta">{{ formatEventDate(ev.startAt) }}</p>
                  <p class="event-location">{{ ev.location || 'Место уточняется' }}</p>
                  <span v-if="ev.isPaid" class="event-paid-badge">Платное</span>
                  <span v-if="isSoldOut(ev)" class="event-soldout-badge">Sold out</span>
                  <span v-if="isRegistered(ev)" class="event-registered-badge">Записан</span>
                  <span v-if="registrationsBadge(ev)" class="event-count-badge">{{ registrationsBadge(ev) }}</span>
                </div>
              </div>
            </ion-card>
            <ion-card
              v-for="n in (upcomingEvents.length === 0 && isLoadingEvents ? 4 : 0)"
              :key="'event-skeleton-' + n"
              class="event-card event-card--skeleton"
            >
              <div class="event-skeleton-wrapper">
                <ion-skeleton-text :animated="true" style="width: 100%; height: 100%;"></ion-skeleton-text>
              </div>
            </ion-card>
          </div>
        </div>
        <div class="events-all-link" v-if="upcomingEvents.length > 0">
          <router-link to="/events">Все события →</router-link>
        </div>
        <div v-else-if="!isLoadingEvents" class="events-empty">
          <p class="events-empty-text">Нет предстоящих событий.</p>
          <router-link to="/events" class="events-empty-link">Перейти к событиям</router-link>
        </div>
      </div>

      <!-- Programs Section -->
      <div class="programs-section" v-if="!globalContext.offlineMode">
        <h2 class="section-title">Программы</h2>
        <div class="programs-grid">
          <ion-card 
            v-for="program in availablePrograms" 
            :key="program.shortName" 
            class="program-card"
            @click="navigateToProgram(program)"
          >
            <div class="program-image-container">
              <div v-if="!programLogos[program.shortName] && isLoadingLogos" class="program-skeleton-wrapper">
                <ion-skeleton-text :animated="true" style="width: 100%; height: 100%;"></ion-skeleton-text>
              </div>
              <img 
                v-else
                :src="programLogos[program.shortName] || fallbackLogo" 
                :alt="program.name"
                class="program-image"
                @load="onProgramImageLoad(program.shortName)"
                @error="onProgramImageError(program.shortName)"
              />
              <div class="program-overlay">
                <p class="program-description">{{ program.description }}</p>
              </div>
            </div>
            </ion-card>
        </div>
      </div>


      <!-- Offline Mode Section -->
      <div v-if="globalContext.offlineMode" class="offline-section">
        <div class="offline-content">
          <ion-icon :icon="cloudOffline" class="offline-icon"></ion-icon>
          <h3 class="offline-title">РЕЖИМ ОФФЛАЙН</h3>
          <ion-button class="reconnect-button" @click="reconnect()" :disabled="reconnecting">
          <ion-icon slot="icon-only" :icon="refreshOutline" v-show="!reconnecting"></ion-icon>
          <ion-spinner name="bubbles" v-show="reconnecting"></ion-spinner>
        </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>


<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import AccountService from "@/services/account.service";
import LogoService from "@/services/logo.service";
import store from '@/store/index';
import { CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonCard,
  IonContent,
  IonIcon,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonSpinner
} from "@ionic/vue";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  calendarOutline,
  cloudOffline,
  refreshOutline
} from "ionicons/icons";
import { defineComponent, inject } from "vue";

// import required modules
import { ILigaEvent } from "@/model/liga-event.model";
import eventsService from "@/services/events.service";

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: "Today",
  components: {
    IonContent,
    IonPage,
    IonCard,
    IonIcon,
    IonSpinner,
    IonButton,
    IonRefresher,
    IonRefresherContent,
    IonSkeletonText,
  },

  setup() {
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const now = dayjs().format("dd DD.MM.YYYY");
    const logoService = new LogoService();

    return {
      globalContext,
      refreshOutline,
      cloudOffline,
      isMobile,
      now,
      logoService,
    };
  },
  data() {
    return {
      reconnecting: false,
      accountService: new AccountService(),
      isLoadingLogos: false,
      upcomingEvents: [] as ILigaEvent[],
      isLoadingEvents: false,
      /** id событий, на которые пользователь записан (для пина «Записан»). */
      registeredEventIds: [] as number[],
      eventsService,
      calendarOutline,
      programLogos: {} as Record<string, string>,
      fallbackLogo: require('@/theme/liga-logo-white.png'),
      availablePrograms: [
        { shortName: 'HD', name: 'Hypedance', description: 'Танцевальные тренировки' },
        { shortName: 'FB', name: 'Functional Ballet', description: 'Функциональный балет' },
        { shortName: 'PP', name: 'Power Pump', description: 'Силовая тренировка' },
        { shortName: 'PLS', name: 'Pilates', description: 'Пилатес' },
        { shortName: 'LSTR', name: 'Liga Stretch', description: 'Стретчинг' },
        { shortName: 'WORKSHOP', name: 'Workshop', description: 'Мастер-классы' },
      ],
    };
  },

  ionViewWillEnter() {
    if (!this.globalContext.offlineMode) {
      this.loadProgramLogos();
      this.loadEvents();
    } else {
      this.loadProgramLogos();
    }
  },

  ionViewDidEnter() {
    if (!this.globalContext.offlineMode) {
      this.loadProgramLogos();
      this.loadEvents();
    } else {
      this.loadProgramLogos();
    }
  },

  computed: {
    isAuthenticated: () => {
      return store.getters.authenticated;
    }
  },

  watch: {
    isAuthenticated: {
      handler(val) {
        if (val && !this.globalContext.offlineMode) this.loadEvents();
      },
      immediate: true
    },
    '$route.name'(name) {
      if (name === 'Today' && !this.globalContext.offlineMode) {
        this.loadProgramLogos();
        this.loadEvents();
      } else if (name === 'Today') {
        this.loadProgramLogos();
      }
    }
  },

  methods: {
    async loadProgramLogos() {
      this.isLoadingLogos = true;
      const loadPromises = this.availablePrograms.map(async (program) => {
        try {
          const logoUrl = await this.logoService.getProgramLogo(program.shortName);
          this.programLogos[program.shortName] = logoUrl;
        } catch (error) {
          console.error(`Failed to load logo for ${program.shortName}:`, error);
          this.programLogos[program.shortName] = this.fallbackLogo;
        }
      });
      
      await Promise.allSettled(loadPromises);
      this.isLoadingLogos = false;
    },

    onProgramImageLoad() {
      // Image loaded successfully
    },

    onProgramImageError(shortName: string) {
      // Fallback to default logo on error
      this.programLogos[shortName] = this.fallbackLogo;
    },

    async navigateToProgram(program?: { name: string; shortName: string }) {
      if (program && program.name) {
        // Store the selected program name to expand the accordion
        await Preferences.set({
          key: "selectedProgram",
          value: program.name,
        });
      }
      this.$router.push('/programs');
    },

    onRefresh(refreshEvent?: CustomEvent) {
      if (refreshEvent?.target) {
        this.loadProgramLogos();
        this.loadEvents();
        (refreshEvent.detail as { complete: () => void }).complete();
      }
    },

    isSoldOut(ev: import('@/model/liga-event.model').ILigaEvent): boolean {
      if (ev.maxRegistrations == null) return false;
      const count = ev.registrationsCount ?? 0;
      return count >= ev.maxRegistrations;
    },

    isRegistered(ev: import('@/model/liga-event.model').ILigaEvent): boolean {
      return this.registeredEventIds.includes(ev.id);
    },

    /** Пин с числом мест: "10/40" для лимитированных, "15" для нелимитированных. */
    registrationsBadge(ev: import('@/model/liga-event.model').ILigaEvent): string {
      const count = ev.registrationsCount ?? 0;
      if (ev.maxRegistrations != null) return `${count}/${ev.maxRegistrations}`;
      return String(count);
    },

    async loadEvents() {
      if (!this.isAuthenticated) return;
      this.isLoadingEvents = true;
      try {
        const [events, registrations] = await Promise.all([
          eventsService.getEvents(false),
          eventsService.getMyRegistrations()
        ]);
        this.upcomingEvents = events;
        this.registeredEventIds = registrations.map((r) => r.ligaEventId);
      } catch {
        this.upcomingEvents = [];
        this.registeredEventIds = [];
      }
      this.isLoadingEvents = false;
    },

    formatEventDate(iso: string) {
      return dayjs(iso).locale("ru").format("D MMMM, HH:mm");
    },
    async reconnect() {
      this.reconnecting = true;
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      const timeout = setTimeout(() => {
        this.reconnecting = false;
      }, 5000);

      CapacitorHttp.request({
        method: "GET",
        url: `${process.env.VUE_APP_API_URL}api/test-connection`,
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(() => {
          this.globalContext.offlineMode = false;
          this.accountService
            .hasAnyAuthorityAndCheckAuth(process.env.VUE_APP_INSTRUCTOR_ROLE)
            .then((response) => {
              if (response) {
                this.accountService
                  .hasAnyAuthorityAndCheckAuth("ROLE_MANAGER")
                  .then((value) => {
                    this.globalContext.isManager = value;
                    this.$router.replace("/programs");
                  });
              } else {
                this.$router.replace("/login");
              }
            });
        })
        .finally(() => {
          this.reconnecting = false;
          clearTimeout(timeout);
        });
    },

    setOfflineMode() {
      this.globalContext.offlineMode = true;
    },
  },
});
</script>

<style scoped>
.today-content {
  --padding-top: 1vh;
  --padding-start: 15px;
  --padding-end: 15px;
}

.today-header {
  margin-bottom: 2rem;
}

.today-header h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.today-header h3 {
  font-size: 1.2rem;
  opacity: 0.8;
  font-weight: 300;
}

/* Section Titles */
.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 2rem 0 1rem 0;
  text-align: center;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  border-radius: 2px;
}

/* Events Section — одна строка, горизонтальный скролл */
.events-section {
  margin-bottom: 3rem;
}

.events-scroll-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  padding: 0 0 8px 0;
  position: relative;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
}

.events-scroll-wrap::-webkit-scrollbar {
  height: 6px;
}
.events-scroll-wrap::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
}
.events-scroll-wrap::-webkit-scrollbar-thumb {
  background: rgba(255, 107, 53, 0.5);
  border-radius: 3px;
}

.events-scroll {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  padding: 0 15px;
  width: max-content;
  min-width: 100%;
}

.event-card {
  flex: 0 0 auto;
  width: 320px;
  min-width: 320px;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  height: 200px;
  cursor: pointer;
}

.event-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(255, 165, 0, 0.2);
}

.event-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.event-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.event-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 107, 53, 0.2);
}

.event-image-placeholder ion-icon {
  font-size: 3rem;
  color: var(--ion-color-orange);
}

.event-card:hover .event-image {
  transform: scale(1.05);
}

.event-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  min-height: 55%;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.4) 25%,
    rgba(0, 0, 0, 0.75) 60%,
    rgba(0, 0, 0, 0.95) 100%
  );
  padding: 1rem;
  color: white;
}

.event-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.3rem 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.event-meta {
  font-size: 0.9rem;
  opacity: 0.95;
  margin: 0 0 0.2rem 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.event-location {
  font-size: 0.8rem;
  opacity: 0.9;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.event-paid-badge {
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(255, 165, 0, 0.9);
  color: #1a1a1a;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
}

.event-soldout-badge {
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(220, 47, 2, 0.95);
  color: #fff;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
  margin-left: 6px;
}

.event-registered-badge {
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(45, 211, 111, 0.9);
  color: #1a1a1a;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
  margin-left: 6px;
}

.event-count-badge {
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
  margin-left: 6px;
}

.events-all-link {
  text-align: center;
  margin-top: 1rem;
}

.events-all-link a {
  color: var(--ion-color-orange);
  font-weight: 600;
  text-decoration: none;
}

.events-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}
.events-empty-text {
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 0.5rem 0;
}
.events-empty-link {
  color: var(--ion-color-orange);
  font-weight: 600;
  text-decoration: none;
}

.event-skeleton-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Programs Section */
.programs-section {
  margin-bottom: 3rem;
}

.programs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  padding: 0 10px;
}

.program-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  height: 180px;
  cursor: pointer;
}

.program-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(255, 165, 0, 0.2);
}

.program-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.program-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  background: rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease;
}

.program-card:hover .program-image {
  transform: scale(1.1);
}

.program-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 1rem;
  color: white;
  text-align: center;
}

.program-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.program-description {
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.3;
}


/* Offline Section */
.offline-section {
  margin-top: 2rem;
  padding: 2rem 1rem;
}

.offline-content {
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.offline-icon {
  font-size: 3rem;
  color: var(--ion-color-orange);
  margin-bottom: 1rem;
}

.offline-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.reconnect-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  width: 60px;
  height: 60px;
  --border-radius: 30px;
}

.reconnect-button ion-icon {
  color: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .today-header h1 {
    font-size: 2rem;
  }
  
  .today-header h3 {
    font-size: 1rem;
  }
  
  .section-title {
    font-size: 1.3rem;
  }
  
  .programs-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
  
  .program-card {
    height: 160px;
  }

  .event-card {
    width: 300px;
    min-width: 300px;
    height: 180px;
  }
}

@media (min-width: 1024px) {
  .programs-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Skeleton Loaders */
.program-skeleton-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Dark mode enhancements */
@media (prefers-color-scheme: dark) {
  .program-card,
  .event-card,
  .offline-content {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}
</style>